import { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { PageHeader, Spinner, Alert } from "@/components/common";
import ConnectionStatus from "@/components/settings/ConnectionStatus";
import CredentialsForm from "@/components/settings/CredentialsForm";
import SetupGuide from "@/components/settings/SetupGuide";
import AccountPanel from "@/components/settings/AccountPanel";
import SyncSummary from "@/components/settings/SyncSummary";
import AccountSecurity from "@/components/settings/AccountSecurity";
import LandingDomain from "@/components/settings/LandingDomain";
import DataTransfer from "@/components/settings/DataTransfer";
import GuardrailsPanel from "@/components/settings/GuardrailsPanel";
import JobsPanel from "@/components/settings/JobsPanel";
import { TrackingCheck } from "@/components/TrackingCheck";
import LeadNudges from "@/components/settings/LeadNudges";
import ClinicHours from "@/components/settings/ClinicHours";
import TrimHoursSettings from "@/components/settings/TrimHoursSettings";
import ServingAlerts from "@/components/settings/ServingAlerts";
import BookingValues from "@/components/settings/BookingValues";
import WebsiteTracking from "@/components/settings/WebsiteTracking";
import { QuotaMeter } from "@/components/QuotaMeter";
import { api } from "@/lib/api";

const HINTS = {
  access_denied: "Access was declined, or your Google account isn't listed as a test user on the OAuth consent screen.",
  redirect_uri_mismatch: "The redirect URI registered on the OAuth client doesn't exactly match the one shown on this page.",
  invalid_client: "Google doesn't recognise the Client ID/Secret. Re-copy both from the same OAuth client.",
  invalid_grant: "The authorization code expired or was already used. Press Connect again.",
  invalid_state: "The connect link expired (10 min). Press Connect again.",
  no_refresh_token: "Remove the app at myaccount.google.com/permissions, then connect again.",
};

const OAuthBanner = ({ result, onClose }) => (
  <div className="relative mb-6" data-testid="oauth-result-banner">
    {result.ok ? (
      <Alert tone="emerald" title="Google Ads connected" testid="oauth-success-banner">Authorization succeeded and the refresh token is stored. Pick an account below.</Alert>
    ) : (
      <Alert tone="red" title={`Google Ads connection failed${result.code ? ` · ${result.code}` : ""}`} testid="oauth-error-banner">
        <div>{result.message}</div>
        {HINTS[result.code] && <div className="mt-1 opacity-80">{HINTS[result.code]}</div>}
      </Alert>
    )}
    <button data-testid="oauth-banner-close" onClick={onClose} className="absolute right-3 top-3 opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
  </div>
);

export default function Settings() {
  const { status, refreshStatus } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [oauthResult, setOauthResult] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [range, setRange] = useState("launch");
  const connected = Boolean(status?.connected);
  const accountId = status?.selected_account?.customer_id;

  useEffect(() => {
    if (!connected || !accountId) return setSnapshot(null);
    api.get("/google-ads/snapshot").then((r) => setSnapshot(r.data.snapshot)).catch(() => {});
  }, [connected, accountId]);

  useEffect(() => {
    const result = params.get("google_ads");
    if (!result) return;
    setOauthResult({ ok: result === "connected", code: params.get("error_code"), message: params.get("message") || "Unknown error" });
    setParams({}, { replace: true });
    refreshStatus();
  }, [params, setParams, refreshStatus]);

  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow="Settings · Google Ads"
        title="Connect & Data"
        description="Link your Google Ads manager account with OAuth. Data syncs automatically every 24 hours. Changes to Google Ads only happen through the Approvals queue."
      />
      {oauthResult && <OAuthBanner result={oauthResult} onClose={() => setOauthResult(null)} />}
      {connected && accountId && <SyncSummary snapshot={snapshot} range={range} setRange={setRange} />}
      {!status ? (
        <Spinner />
      ) : (
        <div className="space-y-6">
          <div className="grid gap-6 xl:grid-cols-5">
            <div className="space-y-6 xl:col-span-3">
              <ConnectionStatus status={status} onChange={refreshStatus} />
              <CredentialsForm status={status} onSaved={refreshStatus} />
            </div>
            <div className="xl:col-span-2">
              <SetupGuide status={status} />
            </div>
          </div>
          {status.connected && <AccountPanel status={status} refreshStatus={refreshStatus} snapshot={snapshot} setSnapshot={setSnapshot} range={range} setRange={setRange} />}
          <LandingDomain />
          <GuardrailsPanel />
          <QuotaMeter editable testid="settings-quota-meter" />
          <JobsPanel />
          <TrackingCheck testid="settings-tracking-check" />
          <ClinicHours />
          <TrimHoursSettings />
          <LeadNudges />
          <ServingAlerts />
          <BookingValues />
          {status.connected && <WebsiteTracking />}
          <DataTransfer />
          <AccountSecurity />
        </div>
      )}
    </div>
  );
}
